import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  IconButton,
  ClickAwayListener,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText
} from '@mui/material';
import { Search as SearchIcon, KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { countryCodes } from '../utils/countryCodes';
import { useId } from 'react';

const PhoneInput = ({
  value = '',
  countryCode = '',
  onChange,
  onCountryCodeChange,
  label = "Phone Number",
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  placeholder = 'Enter your phone number',
  id,
  sx = {}
}) => {
  // Validate and sanitize the incoming country code
  const uniqueId = useId();
  const componentId = id || uniqueId;
  const validateCountryCode = (code) => {
    if (!code) return '+1';
    const validCode = countryCodes.find(country => country.code === code);
    return validCode ? code : '+1';
  };

  const [selectedCountryCode, setSelectedCountryCode] = useState(validateCountryCode(countryCode));
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const validCode = validateCountryCode(countryCode);
    if (validCode !== selectedCountryCode) {
      setSelectedCountryCode(validCode);
    }
  }, [countryCode, selectedCountryCode]);

  const handleCountryCodeChange = (newCode) => {
    setSelectedCountryCode(newCode);
    setSearchTerm(''); // Clear search term when selection changes
    setIsOpen(false);
    if (onCountryCodeChange) {
      onCountryCodeChange(newCode);
    }
  };

  const handlePhoneChange = (event) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleDropdownToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Focus search input when opening
        setTimeout(() => {
          if (searchRef.current) {
            searchRef.current.focus();
          }
        }, 100);
      }
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  // Filter countries based on search term
  const filteredCountries = searchTerm 
    ? countryCodes.filter(country => 
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.flag.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : countryCodes;

  // Get selected country display
  const selectedCountry = countryCodes.find(country => country.code === selectedCountryCode);

  // Validate and sanitize the selected country code
  const validCountryCode = selectedCountry ? selectedCountryCode : '+1';
  const displayCountry = selectedCountry || countryCodes.find(country => country.code === '+1');

  return (
    <Box sx={sx}>
      <Grid container spacing={1}>
        <Grid item sx={{ width: 100 }}>
          <Box sx={{ position: 'relative' }}>
            <FormControl size="small">
              <Box
                ref={selectRef}
                onClick={handleDropdownToggle}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.1rem 14px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  bgcolor: '#fff',
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                  '&:hover': {
                    borderColor: disabled ? '#e0e0e0' : '#bdbdbd',
                  },
                  height: 'auto',
                  width: '100%',
                  minHeight: '50px'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {displayCountry && (
                    <>
                      <span style={{ fontSize: '14px' }}>{displayCountry.flag}</span>
                      <span style={{ whiteSpace: 'nowrap', fontWeight: 'normal', fontSize: '14px' }}>{displayCountry.code}</span>
                    </>
                  )}
                </Box>
                <ArrowDownIcon sx={{ color: '#000', fontSize: '16px', flexShrink: 0 }} />
              </Box>
            </FormControl>

            {/* Custom Dropdown */}
            {isOpen && (
              <ClickAwayListener onClickAway={handleClickAway}>
                <Paper
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    bgcolor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mt: 1,
                    maxHeight: 400,
                    overflow: 'hidden',
                    minWidth: 200,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                >
                  {/* Search Input */}
                  <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
                    <TextField
                      ref={searchRef}
                      fullWidth
                      size="small"
                      placeholder="Search countries..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#fff',
                          '& fieldset': { borderColor: '#e0e0e0' },
                          '&:hover fieldset': { borderColor: '#bdbdbd' },
                          '&.Mui-focused fieldset': { borderColor: '#000' },
                        },
                        '& .MuiInputBase-input': {
                          color: '#000',
                          fontSize: '14px',
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchIcon sx={{ color: '#666' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Country List */}
                  <List sx={{ maxHeight: 300, overflow: 'auto', p: 0 }}>
                    {filteredCountries.map((country) => (
                      <ListItem key={`${country.code}-${country.flag}`} sx={{ p: 0 }}>
                        <ListItemButton
                          onClick={() => handleCountryCodeChange(country.code)}
                          selected={selectedCountryCode === country.code}
                          sx={{
                            color: '#000',
                            fontSize: '14px',
                            fontWeight: 500,
                            padding: '8px 12px',
                            '&:hover': {
                              bgcolor: '#f5f5f5',
                            },
                            '&.Mui-selected': {
                              bgcolor: '#000',
                              color: '#fff',
                              '&:hover': {
                                bgcolor: '#333',
                              }
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{country.flag}</span>
                                <span style={{ whiteSpace: 'nowrap' }}>{country.code}</span>
                                <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>
                                  {country.name}
                                </span>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                    
                    {/* No results message */}
                    {filteredCountries.length === 0 && (
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 2 }}>
                              <span style={{ color: '#666' }}>No countries found</span>
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </ClickAwayListener>
            )}
          </Box>
        </Grid>
        <Grid item xs>
          <TextField
            fullWidth
            id={componentId}
            label={label}
            value={value}
            onChange={handlePhoneChange}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            autoComplete="tel"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { 
                  borderColor: (theme) => error ? theme.palette.error.main : theme.palette.divider
                },
                '&:hover fieldset': { 
                  borderColor: (theme) => error ? theme.palette.error.main : theme.palette.divider
                },
                '&.Mui-focused fieldset': { 
                  borderColor: (theme) => error ? theme.palette.error.main : theme.palette.divider
                },
                bgcolor: '#fff',
                borderRadius: 1,
              },
              '& .MuiOutlinedInput-input': {
                py: 0.7,
                fontSize: 14,
                height: '40px'
              },
              '& .MuiFormHelperText-root': {
                color: error ? '#f44336' : '#666',
                fontSize: 12,
              }
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PhoneInput; 